import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { type FocusEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
    TextInput,
    type TextInputProps,
} from '@myphx-engineering/phx-design-system';
import { useForm } from '@myphx-engineering/phx-design-system/hooks';

import { CONSTANTS } from '../../constants';

export type FormWithPhoneNumber = { phoneNumber: string };
export type PhoneNumberFieldProps<T extends FormWithPhoneNumber> = {
    form: ReturnType<typeof useForm<T>>;
    label?: string;
    page: string;
};

const phoneNumberSchema = z
    .string()
    .refine((val) => /^\d{10}$/.test(val))
    .transform(
        (val) => `(${val.slice(0, 3)}) ${val.slice(3, 6)}-${val.slice(6)}`
    );

export const PhoneNumberField = <
    T extends FormWithPhoneNumber = FormWithPhoneNumber,
>({
    form,
    label,
    page,
}: PhoneNumberFieldProps<T>) => {
    const { t } = useTranslation();
    const appInsights = useAppInsightsContext();
    const [searchParams] = useSearchParams();

    const prescriberOrderId =
        searchParams.get('prescriberOrderId') ??
        searchParams.get('drxId') ??
        undefined;

    const track = useTrackEvent(appInsights, `${page}_PHONE_CLICK`, {});

    const removeCountryCode = (
        phoneNumber: string | undefined | null
    ): string => {
        if (!phoneNumber) return '';

        if (phoneNumber.startsWith('+1')) {
            return phoneNumber.substring(2);
        }

        return phoneNumber;
    };

    const props = form.getInputProps('phoneNumber');
    props.onFocus = () => {
        track({ message: 'Phone number field is clicked' });
        form.getInputProps('phoneNumber').onFocus();
    };
    props.onBlur = (e: FocusEvent<HTMLInputElement>) => {
        // Autofill can change the value of the field without triggering onChange
        if (e.target.value !== form.values.phoneNumber) {
            // @ts-expect-error -- I don't know why TypeScript can't figure this out
            form.setFieldValue('phoneNumber', e.target.value);
        }
        form.getInputProps('phoneNumber').onBlur();
    };

    const onPaste: TextInputProps['onPaste'] = (e) => {
        const clipboardData = e.clipboardData.getData('Text');
        const parsedData = phoneNumberSchema.safeParse(clipboardData);
        form.setFieldValue(
            'phoneNumber',
            // @ts-expect-error -- I don't know why TypeScript can't figure this out
            parsedData.success ? parsedData.data : clipboardData
        );
    };

    // Runs once on mount to format a pre-filled phone number. 'form' is a fresh object each render, so listing it as a dep causes an infinite setFieldValue loop.
    useEffect(() => {
        if (form.values.phoneNumber) {
            const parsedData = phoneNumberSchema.safeParse(
                removeCountryCode(form.values.phoneNumber)
            );
            form.setFieldValue(
                'phoneNumber',
                // @ts-expect-error -- I don't know why TypeScript can't figure this out
                parsedData.success ? parsedData.data : form.values.phoneNumber
            );
        }
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    return (
        <TextInput
            key={form.key('phoneNumber')}
            name="phoneNumber"
            label={label || t('createAccountForm.phoneNumber')}
            description={
                prescriberOrderId
                    ? t('createAccountForm.phoneNumberDesc')
                    : undefined
            }
            placeholder={CONSTANTS.phonePlaceHolder}
            mask={CONSTANTS.phonePlaceHolder}
            required
            onPaste={onPaste}
            {...props}
        />
    );
};
